import { compareNullsLast, orderingDir, orderingField } from "@/shared/lib/ordering";
import type {
  ActivityEvent,
  ActivityPeriod,
  BalanceAdjustBody,
  CustomerDetail,
  CustomerListItem,
  CustomerListParams,
  CustomerPremium,
  CustomerStatus,
  CustomerUpdate,
  DeveloperToggleBody,
  GrantPremiumBody,
  Paginated,
  TimelineEvent,
} from "./types";
import { ACTIVITY_PAGE_SIZE, PAGE_SIZE } from "./types";

// Matches the backend PremiumSettings.price used in tests — keeps the mock paid-grant
// guard consistent with the real flow (deduct from balance, reject when too low).
const MOCK_PREMIUM_PRICE = 50_000;

// Tag a thrown error with a stable op-code; api.ts normalises it into an OpError.
function opError(code: string): Error & { opCode: string } {
  const err = new Error(code) as Error & { opCode: string };
  err.opCode = code;
  return err;
}

// In-memory mock backend used until the real /api/support/* endpoints are live.
// Mirrors the documented contract exactly (DRF page pagination, same fields), so
// flipping to the real API is a one-line swap in api.ts (USE_MOCK = false).

const FIRST = [
  "Jahongir", "Dilnoza", "Sardor", "Maftuna", "Bekzod", "Nigora", "Aziz",
  "Shahnoza", "Otabek", "Kamola", "Rustam", "Zarina", "Ulug‘bek", "Madina",
  "Jasur", "Sevara", "Akmal", "Gulnoza", "Farrux", "Malika",
];
const LAST = [
  "Akromov", "Karimova", "Yusupov", "Tursunova", "Rahimov", "Olimova",
  "Ergashev", "Sobirova", "Qodirov", "Hamidova", "Nazarov", "Islomova",
  "Saidov", "Abdullayeva", "To‘lqinov", "Mirzayeva", "Hakimov",
  "Yo‘ldosheva", "Boburov", "Rashidova",
];
const STATUSES: CustomerStatus[] = ["active", "active", "active", "inactive", "new"];

function minutesAgoIso(mins: number): string {
  return new Date(Date.now() - mins * 60_000).toISOString();
}

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

// Default list order = status priority then newest registration (mirrors the backend
// status_order Case/When + "-date_joined"). NEW leads so the sales team sees fresh signups.
const STATUS_PRIORITY: Record<CustomerStatus, number> = {
  new: 0,
  active: 1,
  inactive: 2,
};

const ACTIVITY = [12, 60, 180, 1440, 2880, 7200, 10080];

// Deterministic premium end-date pool — fixed strings so the mock DB is stable across reloads.
const PREMIUM_DATES = [
  "2026-09-01", "2026-10-15", "2026-11-30", "2027-01-01",
  "2027-03-20", "2027-06-10", "2026-12-31", "2027-02-14",
];

const DB: CustomerDetail[] = Array.from({ length: 48 }, (_, i) => {
  const first = FIRST[(i * 7) % FIRST.length];
  const last = LAST[(i * 5) % LAST.length];
  const status = STATUSES[i % STATUSES.length];
  const complete = i % 4 !== 0; // some records intentionally incomplete (verify banner)

  // is_premium: every 3rd row (i % 3 === 0) is premium.
  const is_premium = i % 3 === 0;
  // is_developer: every 10th row.
  const is_developer = i % 10 === 0;
  // free_premiums_count: deterministic, 0–4 range.
  const free_premiums_count = (i * 3) % 5;
  // premium object present iff is_premium; internally consistent.
  const premium: CustomerPremium | null = is_premium
    ? {
        end_date: PREMIUM_DATES[i % PREMIUM_DATES.length],
        is_free: i % 6 === 0,
        is_repeatable: i % 2 === 0,
        active: true,
      }
    : null;
  // balance: string-encoded decimal, varied per index (simulates real DRF Decimal output).
  const balance = ((i * 137500) % 10_000_000).toFixed(2);

  return {
    id: 10482 - i,
    first_name: first,
    last_name: last,
    phone_number: `+998 9${i % 9} ${100 + ((i * 7) % 900)} ${10 + ((i * 3) % 90)} ${10 + ((i * 9) % 90)}`,
    status,
    loans_count: (i * 3) % 9,
    last_login: status === "new" ? null : minutesAgoIso(ACTIVITY[i % ACTIVITY.length]),
    // OTP logins never touch last_login — some "new" users still logged in once.
    last_login_at:
      status === "new"
        ? i % 2 === 0 ? minutesAgoIso(35 + i) : null
        : minutesAgoIso(ACTIVITY[i % ACTIVITY.length]),
    // Deterministic descending registration: lower index (higher id) = newer signup,
    // so the default status-first sort still has a meaningful time tiebreak per group.
    date_joined: daysAgoIso(i * 3),
    pin: complete ? `3${1012003456789 + i * 271}` : null,
    passport_number: complete ? `AA ${1234567 + i * 131}` : null,
    birth_date: complete ? `199${i % 9}-0${1 + (i % 8)}-1${i % 9}` : null,
    address: complete ? "Toshkent sh., Chilonzor t., 12-uy" : null,
    balance,
    is_premium,
    is_developer,
    free_premiums_count,
    premium,
  };
});

function fullName(c: CustomerListItem): string {
  return `${c.first_name} ${c.last_name}`;
}

export function mockList(params: CustomerListParams): Paginated<CustomerListItem> {
  const q = params.search.trim().toLowerCase();
  const filtered = DB.filter((c) => {
    const matchQ =
      !q ||
      `${fullName(c)} ${c.phone_number} ${c.id}`.toLowerCase().includes(q);
    const matchS = params.status === "all" || c.status === params.status;
    return matchQ && matchS;
  });
  // No ordering = status-first, then newest registration within each group — identical to the
  // backend default. A header click sorts by that column (nulls last, id tie-break like the API).
  // `.filter` already returned a fresh array, so this sort is local.
  const field = orderingField(params.ordering);
  const dir = orderingDir(params.ordering);
  filtered.sort((a, b) => {
    if (field) {
      const value = (c: CustomerDetail) =>
        field === "status_order" ? STATUS_PRIORITY[c.status] : c[field as keyof CustomerDetail];
      return compareNullsLast(value(a), value(b), dir) || a.id - b.id;
    }
    const byStatus = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status];
    if (byStatus !== 0) return byStatus;
    return b.date_joined.localeCompare(a.date_joined); // ISO strings: desc = newest first
  });
  const count = filtered.length;
  const start = (params.page - 1) * PAGE_SIZE;
  const results = filtered.slice(start, start + PAGE_SIZE).map((c) => ({
    id: c.id,
    first_name: c.first_name,
    last_name: c.last_name,
    phone_number: c.phone_number,
    status: c.status,
    loans_count: c.loans_count,
    last_login: c.last_login,
    last_login_at: c.last_login_at,
    is_premium: c.is_premium,
  }));
  return { count, next: null, previous: null, results };
}

export function mockDetail(id: number): CustomerDetail {
  const c = DB.find((x) => x.id === id);
  if (!c) throw new Error("not-found");
  return { ...c };
}

export function mockUpdate(id: number, patch: CustomerUpdate): CustomerDetail {
  const c = DB.find((x) => x.id === id);
  if (!c) throw new Error("not-found");
  // Simulate a unique-conflict on a sentinel phone to exercise the error path.
  if (patch.phone_number && patch.phone_number.replace(/\D/g, "").endsWith("0000")) {
    const err = new Error("conflict") as Error & { isConflict: boolean };
    err.isConflict = true;
    throw err;
  }
  Object.assign(c, patch);
  return { ...c };
}

// ── Sensitive operations (Faza B — 0063.04) ─────────────────────────────────
// Mirror the backend service guards so mock mode exercises the same success/error
// paths the real endpoints enforce. All mutate the in-memory DB row and echo it back.

export function mockSetDeveloper(id: number, body: DeveloperToggleBody): CustomerDetail {
  const c = DB.find((x) => x.id === id);
  if (!c) throw new Error("not-found");
  c.is_developer = body.is_developer;
  return { ...c };
}

export function mockAdjustBalance(id: number, body: BalanceAdjustBody): CustomerDetail {
  const c = DB.find((x) => x.id === id);
  if (!c) throw new Error("not-found");
  const amount = Number(body.amount);
  if (!Number.isFinite(amount) || amount <= 0) throw opError("generic");
  const current = Number(c.balance);
  if (body.direction === "debit" && amount > current) throw opError("balanceLow");
  const next = body.direction === "credit" ? current + amount : current - amount;
  c.balance = next.toFixed(2);
  return { ...c };
}

export function mockGrantPremium(id: number, body: GrantPremiumBody): CustomerDetail {
  const c = DB.find((x) => x.id === id);
  if (!c) throw new Error("not-found");
  if (c.is_premium) throw opError("alreadyPremium");
  if (body.is_free) {
    if (c.free_premiums_count < 1) throw opError("noFreeLeft");
    c.free_premiums_count -= 1;
  } else {
    if (Number(c.balance) < MOCK_PREMIUM_PRICE) throw opError("balanceLow");
    c.balance = (Number(c.balance) - MOCK_PREMIUM_PRICE).toFixed(2);
  }
  c.is_premium = true;
  c.premium = {
    end_date: PREMIUM_DATES[id % PREMIUM_DATES.length],
    is_free: body.is_free,
    is_repeatable: false,
    active: true,
  };
  return { ...c };
}

// ── Timeline mock (task 0066) ────────────────────────────────────────────────
// Deterministic per id, mirroring the real /timeline/ contract: a synthetic
// `registered` (from date_joined), one `status_change`, and three `support_action`
// rows (developer toggle, balance credit, premium grant). `action` values match the
// backend ActionLog constants so the drawer's label map keys off the same strings.
// Returned newest-first, exactly as the backend sorts across all sources.
const MOCK_OPERATOR = "Jahongir A.";

export function mockTimeline(id: number): TimelineEvent[] {
  const c = DB.find((x) => x.id === id);
  if (!c) return [];

  const events: TimelineEvent[] = [
    {
      kind: "registered",
      timestamp: c.date_joined,
      actor_id: null,
      actor_name: null,
      actor_type: null,
      action: null,
      field: null,
      old: null,
      new: null,
      extra: null,
    },
    {
      kind: "status_change",
      timestamp: daysAgoIso((id % 7) + 3),
      actor_id: null,
      actor_name: null,
      actor_type: null,
      action: null,
      field: "status",
      old: "NEW",
      new: "ACTIVE",
      extra: null,
    },
    {
      kind: "support_action",
      timestamp: minutesAgoIso(((id % 5) + 1) * 1440),
      actor_id: 1,
      actor_name: MOCK_OPERATOR,
      actor_type: "support",
      action: "user.support_developer_toggle",
      field: null,
      old: null,
      new: null,
      extra: { is_developer: true },
    },
    {
      kind: "support_action",
      timestamp: minutesAgoIso(((id % 4) + 1) * 240),
      actor_id: 1,
      actor_name: MOCK_OPERATOR,
      actor_type: "support",
      action: "user.support_balance_adjust",
      field: null,
      old: null,
      new: null,
      extra: {
        amount: "50000.00",
        direction: "credit",
        reason: "Goodwill credit",
        old: "0.00",
        new: "50000.00",
      },
    },
    {
      kind: "support_action",
      timestamp: minutesAgoIso(((id % 3) + 1) * 60),
      actor_id: 1,
      actor_name: MOCK_OPERATOR,
      actor_type: "support",
      action: "premium.activate",
      field: null,
      old: null,
      new: null,
      extra: null,
    },
  ];

  // Single newest-first ordering across all sources (ISO strings sort chronologically).
  events.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  return events;
}

// ── Customer activity (task 0153) ────────────────────────────────────────────
// Deterministic feed of the customer's own actions (~6 weeks, newest first). `action` /
// `extra` / `target_ct` mirror real ActionLog rows so the drawer's label map is exercised.
type ActivitySeed = Omit<ActivityEvent, "id" | "timestamp" | "category" | "description">;

const ACTIVITY_SEEDS: ActivitySeed[] = [
  { action: "user.login", app: "merchant", target_ct: "users.User", target_id: null, target_repr: "", extra: {} },
  { action: "loan.create", app: "merchant", target_ct: "loans.Loan", target_id: 4210, target_repr: "Loan object (4210)", extra: {} },
  { action: "payment.mark_paid", app: "merchant", target_ct: "payment.Payment", target_id: 88012, target_repr: "MONTHLY_FULL -> 1500000.0", extra: { loan_id: 4188 } },
  { action: "user.client_add", app: "merchant", target_ct: "users.Client", target_id: 9031, target_repr: "merchant client", extra: { client_id: 10431 } },
  { action: "payment.create", app: "client", target_ct: "payment.Payment", target_id: 88034, target_repr: "", extra: { type: "MONTHLY_FULL", loan_id: 4102 } },
  { action: "loan.activate", app: "client", target_ct: "loans.Loan", target_id: 4210, target_repr: "Loan object (4210)", extra: {} },
  { action: "payment.approve", app: "merchant", target_ct: "payment.Payment", target_id: 87990, target_repr: "", extra: { loan_id: 4188, payment_type: "monthly_full", amount: 1_850_000 } },
  { action: "debt.create", app: "merchant", target_ct: "debts.Debt", target_id: 311, target_repr: "", extra: {} },
  { action: "payment.part_create", app: "client", target_ct: "payment.Payment", target_id: 88101, target_repr: "", extra: { loan_id: 4102, amount: 750_000 } },
  { action: "user.profile_update", app: "merchant", target_ct: "users.User", target_id: null, target_repr: "", extra: {} },
  { action: "payment.several_create", app: "client", target_ct: "", target_id: null, target_repr: "", extra: { payment_ids: [88120, 88121], count: 2 } },
  { action: "premium.renew", app: "", target_ct: "premium.Premium", target_id: 702, target_repr: "", extra: {} },
  { action: "debt.cash_update", app: "merchant", target_ct: "debts.DebtWallet", target_id: 58, target_repr: "", extra: { cash: "1240000.00" } },
  { action: "loan.pause", app: "merchant", target_ct: "loans.Loan", target_id: 4102, target_repr: "", extra: { new_status: "paused" } },
  { action: "payment.delay", app: "client", target_ct: "payment.Payment", target_id: 88140, target_repr: "", extra: { loan_id: 4102, next_month: true } },
  { action: "payment.reject", app: "merchant", target_ct: "payment.Payment", target_id: 88141, target_repr: "", extra: { loan_id: 4102, reject_cause: "Chek rasmi xira" } },
  { action: "debt.settle", app: "merchant", target_ct: "debts.DebtPayment", target_id: 298, target_repr: "", extra: { contact_id: 77, type: "POSITIVE", amount: "400000" } },
  { action: "user.register", app: "merchant", target_ct: "users.User", target_id: null, target_repr: "", extra: {} },
];

// Minutes ago per event: dense recent activity, then sparser history (spans ~6 weeks).
const ACTIVITY_OFFSETS = [
  8, 95, 260, 1500, 1720, 2900, 4400, 6100, 8300, 9100,
  11600, 15800, 21400, 28900, 36200, 44100, 52800, 60500,
];

export function mockActivity(id: number, period: ActivityPeriod, page: number): Paginated<ActivityEvent> {
  if (!DB.some((x) => x.id === id)) throw new Error("not-found");
  const shift = (id % 7) * 11; // vary timestamps a little between customers
  const all: ActivityEvent[] = ACTIVITY_SEEDS.map((seed, i) => ({
    ...seed,
    id: `mock-${id}-${i}`,
    timestamp: minutesAgoIso(ACTIVITY_OFFSETS[i] + shift),
    category: seed.action.split(".")[0],
    description: "",
  }));
  if (period === "last5") return { count: 5, next: null, previous: null, results: all.slice(0, 5) };
  const rows = period === "7d" ? all.filter((e) => Date.now() - Date.parse(e.timestamp) < 7 * 86_400_000) : all;
  const start = (page - 1) * ACTIVITY_PAGE_SIZE;
  const hasNext = start + ACTIVITY_PAGE_SIZE < rows.length;
  return {
    count: rows.length,
    next: hasNext ? `mock://activity?page=${page + 1}` : null,
    previous: page > 1 ? `mock://activity?page=${page - 1}` : null,
    results: rows.slice(start, start + ACTIVITY_PAGE_SIZE),
  };
}
